/* eslint-disable camelcase */
import React, { forwardRef, useImperativeHandle, useEffect } from "react"
import {
  Grid,
  TextField,
  Box,
  Paper,
  Typography,
  Autocomplete,
  CircularProgress,
  MenuItem,
  InputAdornment,
} from "@mui/material"
import { HowToVote, Domain } from "@mui/icons-material"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useQuery } from "@tanstack/react-query"

import { api } from "../../../../api"

const FormularioJuntaReceptora = forwardRef(({ onSubmit, isLoading, modo, initialData }, ref) => {
  // ========================================================
  // 1. CONFIGURACIÓN DE FORMIK (VALIDACIONES Y ESTADOS)
  // ========================================================
  const formik = useFormik({
    initialValues: {
      junta_codigoOld: "", // Clave primaria antigua (Necesario para Editar)
      junta_codigo: "",
      reci_codigo: "",
      junta_numero: "",
      junta_genero: "",
      junta_empadronados: "",
    },
    validationSchema: Yup.object({
      junta_codigo: Yup.string().required("El código de junta es obligatorio").max(15, "Máximo 15 caracteres"),
      reci_codigo: Yup.string().required("Seleccione el Recinto Electoral"),
      junta_numero: Yup.number()
        .required("El número de junta es obligatorio")
        .positive("Debe ser mayor a 0")
        .integer("Debe ser un número entero"),
      junta_genero: Yup.string().required("Seleccione el género de la junta"),
      junta_empadronados: Yup.number().required("Ingrese la cantidad de empadronados").min(0, "No puede ser negativo"),
    }),
    onSubmit: async (values) => {
      await onSubmit(values)
    },
  })

  useImperativeHandle(ref, () => ({
    handleSubmit: formik.submitForm,
  }))

  useEffect(() => {
    if (modo === "editar" && initialData) {
      formik.setValues({
        junta_codigoOld: initialData.junta_codigo || "",
        junta_codigo: initialData.junta_codigo || "",
        reci_codigo: initialData.reci_codigo || "",
        junta_numero: initialData.junta_numero || "",
        junta_genero: initialData.junta_genero || "",
        junta_empadronados: initialData.junta_empadronados !== undefined ? initialData.junta_empadronados : "",
      })
    }
  }, [modo, initialData])

  // ========================================================
  // 2. CONSULTAR LISTA DE RECINTOS (REACT-QUERY)
  // ========================================================
  const { data: rawRecintos = [], isFetching: isFetchingRecintos } = useQuery({
    queryKey: ["lista_recintos_dropdown"],
    queryFn: async () => {
      const res = await api.post("/ControlElectoral/getRecintosDropdown", {})
      return res.data?.data || []
    },
    staleTime: 5 * 60 * 1000,
  })

  // ========================================================
  // RENDERIZADO DEL FORMULARIO
  // ========================================================
  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={3}>
        {/* Contenedor Centrado */}
        <Grid item xs={12} md={8} margin="auto">
          <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h6" color="primary" fontWeight="bold" mb={4} display="flex" alignItems="center">
              <HowToVote sx={{ mr: 1, fontSize: 28 }} /> Datos de la Junta Receptora
            </Typography>

            <Grid container spacing={3}>
              {/* COMBO INTELIGENTE DE RECINTOS (AUTOCOMPLETE) */}
              <Grid item xs={12}>
                <Autocomplete
                  options={rawRecintos}
                  // Muestra el código y el nombre en el combo
                  getOptionLabel={(o) => `${o.id} - ${o.label}`}
                  value={rawRecintos.find((i) => i.id === formik.values.reci_codigo) || null}
                  onChange={(e, newValue) => {
                    formik.setFieldValue("reci_codigo", newValue ? newValue.id : "")
                  }}
                  disabled={isLoading || isFetchingRecintos}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Asignar al Recinto Electoral *"
                      placeholder={isFetchingRecintos ? "Cargando recintos..." : "Buscar o seleccionar recinto..."}
                      error={formik.touched.reci_codigo && Boolean(formik.errors.reci_codigo)}
                      helperText={formik.touched.reci_codigo && formik.errors.reci_codigo}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <Domain fontSize="small" sx={{ ml: 1, color: "text.secondary" }} />
                            </InputAdornment>
                            {isFetchingRecintos ? <CircularProgress size={20} sx={{ ml: 1 }} /> : null}
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* CÓDIGO DE JUNTA */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="junta_codigo"
                  name="junta_codigo"
                  label="Código de la Junta *"
                  disabled={modo === "editar" || isLoading}
                  value={formik.values.junta_codigo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.junta_codigo && Boolean(formik.errors.junta_codigo)}
                  helperText={formik.touched.junta_codigo && formik.errors.junta_codigo}
                  inputProps={{ maxLength: 15, style: { textTransform: "uppercase" } }}
                />
              </Grid>

              {/* NÚMERO DE JUNTA */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  id="junta_numero"
                  name="junta_numero"
                  label="Número de Junta *"
                  disabled={isLoading}
                  value={formik.values.junta_numero}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.junta_numero && Boolean(formik.errors.junta_numero)}
                  helperText={formik.touched.junta_numero && formik.errors.junta_numero}
                />
              </Grid>

              {/* GÉNERO DE LA JUNTA */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  id="junta_genero"
                  name="junta_genero"
                  label="Género de la Junta *"
                  disabled={isLoading}
                  value={formik.values.junta_genero}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.junta_genero && Boolean(formik.errors.junta_genero)}
                  helperText={formik.touched.junta_genero && formik.errors.junta_genero}
                >
                  <MenuItem value="">
                    <em>-- Seleccione --</em>
                  </MenuItem>
                  <MenuItem value="M">Masculino</MenuItem>
                  <MenuItem value="F">Femenino</MenuItem>
                  <MenuItem value="X">Mixto / Sin Especificar</MenuItem>
                </TextField>
              </Grid>

              {/* EMPADRONADOS */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  id="junta_empadronados"
                  name="junta_empadronados"
                  label="Cantidad de Empadronados *"
                  disabled={isLoading}
                  value={formik.values.junta_empadronados}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.junta_empadronados && Boolean(formik.errors.junta_empadronados)}
                  helperText={formik.touched.junta_empadronados && formik.errors.junta_empadronados}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </form>
  )
})

export default FormularioJuntaReceptora
